<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  meta: { type: Object, default: () => ({}) },
  conversations: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:currentPage']);
const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const totalCount = computed(() => Number(props.meta.totalCount || 0));
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const accountLabelsValue = computed(() => accountLabels.value);
const conversationContact = conversation => {
  const sender = conversation.meta?.sender || {};
  const contact = contactsById.value(sender.id);
  return contact.id ? contact : sender;
};
const conversationInbox = conversation =>
  stateInbox.value(conversation.inboxId) || {
    name: '',
    channelType: conversation.meta?.channel,
  };
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>

  <div
    v-else-if="conversations.length > 0"
    class="px-6 divide-y divide-n-strong [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
  >
    <ConversationCard
      v-for="conversation in conversations"
      :key="conversation.id"
      :conversation="conversation"
      :contact="conversationContact(conversation)"
      :state-inbox="conversationInbox(conversation)"
      :account-labels="accountLabelsValue"
      class="rounded-none hover:rounded-2xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    />
  </div>

  <p
    v-else
    class="py-8 px-4 mx-6 text-sm text-center rounded-2xl border border-dashed border-n-strong text-n-slate-11"
  >
    {{ t('COMPANIES.DETAIL.HISTORY.EMPTY') }}
  </p>
  <PaginationFooter
    v-if="isWorkspaceV2 && !isLoading && totalCount > 20"
    current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
    :current-page="Number(meta.page || 1)"
    :total-items="totalCount"
    :items-per-page="20"
    class="!h-auto !px-2 [&>div]:gap-1 [&>div>div]:gap-1 min-h-[4.25rem] [&_button]:!h-11 [&_button]:!w-11 [&_button]:!rounded-xl"
    @update:current-page="emit('update:currentPage', $event)"
  />
</template>
